import { useEffect, useState } from "react";
import { useDebouncedValue } from "./useDebouncedValue";

export const MIN_SEARCH_LENGTH = 2;
export const SEARCH_DELAY_MS = 150;

type InvokeApi = (args: Record<string, unknown>) => Promise<any>;
export type SearchStatus = "idle" | "loading" | "done" | "error";

/** Searches an endpoint as the user types: waits for a pause, and ignores answers to queries that are no longer current */
export const useRemoteSearch = <T,>({ invokeApi, endpoint, query }: { invokeApi: InvokeApi; endpoint: string; query: string }) => {
  const trimmed = query.trim();
  const settled = useDebouncedValue(trimmed, SEARCH_DELAY_MS);
  const [result, setResult] = useState<{ status: SearchStatus; data?: T }>({ status: "idle" });

  useEffect(() => {
    if (settled.length < MIN_SEARCH_LENGTH) {
      setResult({ status: "idle" });
      return;
    }
    let current = true;
    setResult((previous) => ({ status: "loading", data: previous.data }));
    invokeApi({ endpoint, data: { query: settled }, silent: true })
      .then((data) => current && setResult({ status: "done", data }))
      .catch(() => current && setResult({ status: "error" }));
    return () => {
      current = false;
    };
  }, [settled, endpoint, invokeApi]);

  const tooShort = trimmed.length < MIN_SEARCH_LENGTH;
  const waiting = !tooShort && trimmed !== settled;
  return {
    status: tooShort ? ("idle" as SearchStatus) : waiting ? ("loading" as SearchStatus) : result.status,
    data: tooShort ? undefined : result.data,
    query: settled,
    tooShort,
  };
};
